import React, { useState, useMemo, useCallback } from 'react';
import { collection, addDoc, getDocs, deleteDoc, doc, updateDoc, query, where } from 'firebase/firestore';
import SupplierOrderModal from './SupplierOrderModal';
import { sortSupplierSessionsByDate } from '../../utils/sessionListSortUtils';
import {
  aggregateSupplierSessionsByCurrency,
  getSupplierSessionRemaining,
  formatSupplierBalanceLabel
} from '../../utils/supplierSessionBalanceUtils';
import {
  filterDirectPaymentsByDateRange,
  formatDealerPdfDateRangeLabel
} from '../../utils/dealerAggregatePdfUtils';
import { generateSupplierDirectPaymentsPDF } from '../../utils/pdfGenerators';

const CURRENCY_OPTIONS = [
  { value: 'TRY', label: '₺ TL', symbol: '₺' },
  { value: 'USD', label: '$ USD', symbol: '$' },
  { value: 'EUR', label: '€ EUR', symbol: '€' }
];

const CURRENCY_SYMBOLS = { TRY: '₺', USD: '$', EUR: '€' };

const getTodayDateStr = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getSessionDateStr = (session) => {
  if (session?.sessionDate) return session.sessionDate;
  if (session?.createdAt?.toDate) {
    const d = session.createdAt.toDate();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  return getTodayDateStr();
};

// Para birimi formatı — TRY için sağda ₺, diğerleri için solda sembol
const fmtCurrency = (v, currency = 'TRY') => {
  const sym = CURRENCY_SYMBOLS[currency] || currency;
  const num = new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v || 0);
  return currency === 'TRY' ? `${num} ₺` : `${sym} ${num}`;
};

const formatSessionProductNames = (productNames = []) => {
  const names = [...new Set(productNames.filter(Boolean))];
  if (!names.length) {
    return { text: '—', title: '' };
  }
  return { text: names.join(', '), title: names.join(', ') };
};

const SupplierSessionPanel = ({
  dealer,
  db,
  user,
  sessions,
  setSessions,
  isLoading,
  fetchSessions,
  onCustomerChange,
  onDealerUpdate
}) => {
  const [selectedSession, setSelectedSession] = useState(null);
  const [deleteSessionConfirm, setDeleteSessionConfirm] = useState(null);
  const [sessionPage, setSessionPage] = useState(1);
  const SESSION_PAGE_SIZE = 10;
  const sortedSessions = useMemo(() => sortSupplierSessionsByDate(sessions), [sessions]);

  // Kur ve tarih — yeni alım listesi oluştururken kullanılacak
  const [newSessionCurrency, setNewSessionCurrency] = useState('TRY');
  const [newSessionDate, setNewSessionDate] = useState(getTodayDateStr);
  const [savingSessionDateId, setSavingSessionDateId] = useState(null);

  // Manuel (Harici) Ödeme Accordion ve Form State'leri
  const [isDirectPaymentAccordionOpen, setIsDirectPaymentAccordionOpen] = useState(false);
  const [newDirectPaymentAmount, setNewDirectPaymentAmount] = useState('');
  const [newDirectPaymentCurrency, setNewDirectPaymentCurrency] = useState('TRY');
  const [newDirectPaymentType, setNewDirectPaymentType] = useState('Nakit');
  const [newDirectPaymentDate, setNewDirectPaymentDate] = useState(getTodayDateStr);
  const [newDirectPaymentNote, setNewDirectPaymentNote] = useState('');
  const [isAddingDirectPayment, setIsAddingDirectPayment] = useState(false);
  const [directPdfDateFrom, setDirectPdfDateFrom] = useState('');
  const [directPdfDateTo, setDirectPdfDateTo] = useState('');
  const [isGeneratingDirectPdf, setIsGeneratingDirectPdf] = useState(false);
  const [directPaymentPage, setDirectPaymentPage] = useState(1);
  const DIRECT_PAYMENT_PAGE_SIZE = 5;

  // Direct Payment Edit / Delete State
  const [editingDirectPayment, setEditingDirectPayment] = useState(null);
  const [editDirectPaymentAmount, setEditDirectPaymentAmount] = useState('');
  const [editDirectPaymentCurrency, setEditDirectPaymentCurrency] = useState('TRY');
  const [editDirectPaymentType, setEditDirectPaymentType] = useState('Nakit');
  const [editDirectPaymentDate, setEditDirectPaymentDate] = useState('');
  const [editDirectPaymentNote, setEditDirectPaymentNote] = useState('');
  const [deleteDirectPaymentConfirm, setDeleteDirectPaymentConfirm] = useState(null);

  // Toast / Alert notification state
  const [panelToast, setPanelToast] = useState({ show: false, message: '', type: 'success' });
  const showToast = useCallback((message, type = 'success') => {
    setPanelToast({ show: true, message, type });
    setTimeout(() => setPanelToast(prev => ({ ...prev, show: false })), 4000);
  }, []);

  const supplierDirectPayments = useMemo(() => {
    return dealer?.supplierDirectPayments || [];
  }, [dealer?.supplierDirectPayments]);

  const handleCreateSession = async () => {
    if (!dealer) return;
    try {
      const dateStr = newSessionDate || getTodayDateStr();

      const sessionData = {
        dealerId: dealer.id,
        dealerName: dealer.companyName,
        sessionDate: dateStr,
        currency: newSessionCurrency,
        paidAmount: 0,
        isPaymentCompleted: false,
        remainingAmount: 0,
        totalOrderAmount: 0,
        paymentHistory: [],
        createdAt: new Date(),
        updatedAt: new Date(),
        createdBy: user?.uid || null
      };

      const docRef = await addDoc(collection(db, 'supplier_sessions'), sessionData);
      const newSession = { id: docRef.id, ...sessionData, productNames: [] };
      setSessions(prev => [newSession, ...prev]);

      setSelectedSession(newSession);
      if (onCustomerChange) {
        onCustomerChange();
      }
    } catch (error) {
      console.error("Tedarikçi oturumu oluşturulurken hata:", error);
    }
  };

  const handleDeleteSession = async () => {
    if (!deleteSessionConfirm) return;
    try {
      const ordersQuery = query(
        collection(db, 'supplier_orders'),
        where('sessionId', '==', deleteSessionConfirm.id)
      );
      const ordersSnapshot = await getDocs(ordersQuery);
      const deletePromises = ordersSnapshot.docs.map(orderDoc =>
        deleteDoc(doc(db, 'supplier_orders', orderDoc.id))
      );
      await Promise.all(deletePromises);

      await deleteDoc(doc(db, 'supplier_sessions', deleteSessionConfirm.id));

      setSessions(prev => prev.filter(s => s.id !== deleteSessionConfirm.id));
      setDeleteSessionConfirm(null);
      if (onCustomerChange) {
        onCustomerChange();
      }
    } catch (error) {
      console.error("Tedarikçi oturumu silinirken hata:", error);
    }
  };

  const handleSessionDateChange = async (sessionId, newDate) => {
    if (!newDate) return;
    setSavingSessionDateId(sessionId);
    try {
      await updateDoc(doc(db, 'supplier_sessions', sessionId), {
        sessionDate: newDate,
        updatedAt: new Date()
      });
      setSessions(prev => prev.map(s =>
        s.id === sessionId ? { ...s, sessionDate: newDate } : s
      ));
      setSelectedSession(prev =>
        prev?.id === sessionId ? { ...prev, sessionDate: newDate } : prev
      );
      if (onCustomerChange) {
        onCustomerChange();
      }
    } catch (err) {
      console.error('Alım tarihi güncellenemedi:', err);
    } finally {
      setSavingSessionDateId(null);
    }
  };

  // Harici Tedarikçi Ödeme İşlemleri (Ekleme, Güncelleme, Silme)
  const handleAddDirectPayment = async (e) => {
    if (e) e.preventDefault();
    if (!dealer || !dealer.id) return;

    const amount = parseFloat(newDirectPaymentAmount);
    if (!amount || amount <= 0) {
      showToast('Lütfen geçerli bir ödeme tutarı girin.', 'warning');
      return;
    }
    if (!newDirectPaymentDate) {
      showToast('Lütfen ödeme tarihini girin.', 'warning');
      return;
    }

    try {
      setIsAddingDirectPayment(true);
      const existingPayments = dealer.supplierDirectPayments || [];
      const newPaymentRecord = {
        id: 'sdp_' + Date.now().toString(),
        amount,
        currency: newDirectPaymentCurrency || 'TRY',
        type: newDirectPaymentType || 'Nakit',
        date: newDirectPaymentDate,
        notes: newDirectPaymentNote || '',
        createdAt: new Date().toISOString(),
        createdBy: user?.uid || null
      };

      const updatedPayments = [newPaymentRecord, ...existingPayments];

      await updateDoc(doc(db, 'dealers', dealer.id), {
        supplierDirectPayments: updatedPayments,
        updatedAt: new Date()
      });

      const updatedDealer = { ...dealer, supplierDirectPayments: updatedPayments };
      if (onDealerUpdate) {
        onDealerUpdate(updatedDealer);
      }

      setNewDirectPaymentAmount('');
      setNewDirectPaymentNote('');
      setDirectPaymentPage(1);

      showToast('Harici ödeme başarıyla kaydedildi.', 'success');
      if (onCustomerChange) onCustomerChange();
    } catch (error) {
      console.error('Harici tedarikçi ödeme ekleme hatası:', error);
      showToast('Ödeme eklenirken hata oluştu: ' + error.message, 'error');
    } finally {
      setIsAddingDirectPayment(false);
    }
  };

  const handleSaveEditedDirectPayment = async () => {
    if (!dealer || !dealer.id || !editingDirectPayment) return;

    const amount = parseFloat(editDirectPaymentAmount);
    if (!amount || amount <= 0) {
      showToast('Lütfen geçerli bir ödeme tutarı girin.', 'warning');
      return;
    }
    if (!editDirectPaymentDate) {
      showToast('Lütfen ödeme tarihini girin.', 'warning');
      return;
    }

    try {
      setIsAddingDirectPayment(true);
      const existingPayments = dealer.supplierDirectPayments || [];
      const updatedPayments = existingPayments.map(p => {
        if (p.id === editingDirectPayment.id) {
          return {
            ...p,
            amount,
            currency: editDirectPaymentCurrency || 'TRY',
            type: editDirectPaymentType,
            date: editDirectPaymentDate,
            notes: editDirectPaymentNote,
            updatedAt: new Date().toISOString()
          };
        }
        return p;
      });

      await updateDoc(doc(db, 'dealers', dealer.id), {
        supplierDirectPayments: updatedPayments,
        updatedAt: new Date()
      });

      const updatedDealer = { ...dealer, supplierDirectPayments: updatedPayments };
      if (onDealerUpdate) {
        onDealerUpdate(updatedDealer);
      }

      setEditingDirectPayment(null);
      showToast('Ödeme bilgisi güncellendi.', 'success');
      if (onCustomerChange) onCustomerChange();
    } catch (error) {
      console.error('Ödeme güncelleme hatası:', error);
      showToast('Güncelleme yapılırken hata oluştu: ' + error.message, 'error');
    } finally {
      setIsAddingDirectPayment(false);
    }
  };

  const handleDeleteDirectPayment = async (paymentId) => {
    if (!dealer || !dealer.id || !paymentId) return;

    try {
      setIsAddingDirectPayment(true);
      const existingPayments = dealer.supplierDirectPayments || [];
      const updatedPayments = existingPayments.filter(p => p.id !== paymentId);

      await updateDoc(doc(db, 'dealers', dealer.id), {
        supplierDirectPayments: updatedPayments,
        updatedAt: new Date()
      });

      const updatedDealer = { ...dealer, supplierDirectPayments: updatedPayments };
      if (onDealerUpdate) {
        onDealerUpdate(updatedDealer);
      }
      setDirectPaymentPage(p => Math.max(1, Math.min(p, Math.ceil((updatedPayments.length || 1) / DIRECT_PAYMENT_PAGE_SIZE))));

      setDeleteDirectPaymentConfirm(null);
      showToast('Harici ödeme silindi.', 'success');
      if (onCustomerChange) onCustomerChange();
    } catch (error) {
      console.error('Ödeme silme hatası:', error);
      showToast('Ödeme silinirken hata oluştu: ' + error.message, 'error');
    } finally {
      setIsAddingDirectPayment(false);
    }
  };

  const handleGenerateDirectPaymentPDF = useCallback((mode) => {
    if (!dealer || isGeneratingDirectPdf) return;

    const allPayments = dealer.supplierDirectPayments || [];

    if (allPayments.length === 0) {
      showToast('Henüz kaydedilmiş harici ödeme bulunmuyor.', 'warning');
      return;
    }

    let filteredPayments = allPayments;
    let dateRangeLabel = 'Tüm Zamanlar';

    if (mode === 'range') {
      if (!directPdfDateFrom || !directPdfDateTo) {
        showToast('Lütfen harici ödemeler PDF için başlangıç ve bitiş tarihlerini seçin.', 'warning');
        return;
      }
      if (new Date(`${directPdfDateFrom}T00:00:00`) > new Date(`${directPdfDateTo}T00:00:00`)) {
        showToast('Başlangıç tarihi bitiş tarihinden sonra olamaz.', 'warning');
        return;
      }
      filteredPayments = filterDirectPaymentsByDateRange(allPayments, directPdfDateFrom, directPdfDateTo, 'range');
      dateRangeLabel = formatDealerPdfDateRangeLabel(directPdfDateFrom, directPdfDateTo, 'range');

      if (filteredPayments.length === 0) {
        showToast('Seçilen tarih aralığında kaydedilmiş harici ödeme bulunamadı.', 'warning');
        return;
      }
    }

    setIsGeneratingDirectPdf(true);
    try {
      generateSupplierDirectPaymentsPDF({
        dealer,
        payments: filteredPayments,
        dateRangeLabel
      });
      showToast('Harici ödemeler PDF raporu oluşturuldu.', 'success');
    } catch (error) {
      console.error('Harici ödemeler PDF oluşturma hatası:', error);
      showToast('PDF oluşturulurken hata oluştu: ' + error.message, 'error');
    } finally {
      setIsGeneratingDirectPdf(false);
    }
  }, [dealer, isGeneratingDirectPdf, directPdfDateFrom, directPdfDateTo, showToast]);

  // Özet kartları: aynı para birimindeki session ve harici ödemeleri grupla
  const currencyGroups = aggregateSupplierSessionsByCurrency(sessions, supplierDirectPayments);
  const currencyGroupKeys = Object.keys(currencyGroups);

  // Accordion başlığı için toplam özet metni
  const totalDirectPaidByCurrencies = useMemo(() => {
    const totals = (supplierDirectPayments || []).reduce((acc, p) => {
      const c = p.currency || 'TRY';
      acc[c] = (acc[c] || 0) + (parseFloat(p.amount || 0) || 0);
      return acc;
    }, {});
    const keys = Object.keys(totals);
    if (!keys.length) return '0,00 ₺';
    return keys.map(k => fmtCurrency(totals[k], k)).join(' + ');
  }, [supplierDirectPayments]);

  const hasAnySupplierData = sessions.length > 0 || supplierDirectPayments.length > 0;

  return (
    <div className="dealer-sessions-section supplier-sessions-panel">
      {/* Toast Alert */}
      {panelToast.show && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          zIndex: 9999,
          padding: '12px 20px',
          borderRadius: '8px',
          background: panelToast.type === 'error' ? '#ef4444' : panelToast.type === 'warning' ? '#f59e0b' : '#10b981',
          color: '#ffffff',
          fontWeight: 600,
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <i className={`fas ${panelToast.type === 'error' ? 'fa-exclamation-circle' : panelToast.type === 'warning' ? 'fa-exclamation-triangle' : 'fa-check-circle'}`}></i>
          {panelToast.message}
        </div>
      )}

      <div className="sessions-header">
        <h4>
          <i className="fas fa-truck-loading"></i> Tedarikçi Alım Listeleri
        </h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          {/* Tarih seçimi */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <label style={{ fontSize: '0.82rem', fontWeight: 600, color: '#64748b', whiteSpace: 'nowrap' }}>
              <i className="fas fa-calendar-alt" style={{ marginRight: '4px', color: '#8b5cf6' }}></i>Tarih:
            </label>
            <input
              type="date"
              className="session-date-input"
              value={newSessionDate}
              onChange={(e) => setNewSessionDate(e.target.value)}
            />
          </div>
          {/* Kur seçimi */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <label style={{ fontSize: '0.82rem', fontWeight: 600, color: '#64748b', whiteSpace: 'nowrap' }}>
              <i className="fas fa-coins" style={{ marginRight: '4px', color: '#8b5cf6' }}></i>Kur:
            </label>
            <select
              value={newSessionCurrency}
              onChange={(e) => setNewSessionCurrency(e.target.value)}
              style={{
                padding: '6px 10px',
                border: '1.5px solid #ddd6fe',
                borderRadius: '8px',
                background: '#f5f3ff',
                color: '#6d28d9',
                fontWeight: 700,
                fontSize: '0.9rem',
                cursor: 'pointer',
                outline: 'none'
              }}
            >
              {CURRENCY_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <button
            className="add-session-btn"
            onClick={handleCreateSession}
            style={{ background: '#8b5cf6', boxShadow: '0 4px 10px rgba(139, 92, 246, 0.2)' }}
          >
            <i className="fas fa-plus-circle"></i> Yeni Alım Listesi
          </button>
        </div>
      </div>

      {/* Özet kartlar — para birimine göre gruplu */}
      {hasAnySupplierData && (
        <>
          {currencyGroupKeys.map(cur => {
            const group = currencyGroups[cur];
            const openBalance = Math.max(0, group.netBalance);
            const creditBalance = Math.max(0, -group.netBalance);
            const isCreditor = group.netBalance < -0.009;
            const isBalanced = Math.abs(group.netBalance) < 0.01;

            return (
            <div key={cur} className="dealer-financial-summary" style={{ marginBottom: currencyGroupKeys.length > 1 ? '8px' : undefined }}>
              {currencyGroupKeys.length > 1 && (
                <div style={{ gridColumn: '1/-1', fontSize: '0.78rem', fontWeight: 700, color: '#8b5cf6', marginBottom: '-5px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ background: '#ede9fe', padding: '2px 8px', borderRadius: '12px' }} title={cur}>{CURRENCY_SYMBOLS[cur] || cur}</span>
                </div>
              )}
              <div className="financial-card total-card" style={{ background: 'linear-gradient(135deg, #f5f3ff, #ede9fe)', borderColor: '#ddd6fe' }}>
                <div className="financial-card-icon" style={{ background: '#ddd6fe', color: '#6b21a8' }}><i className="fas fa-file-invoice-dollar"></i></div>
                <div className="financial-card-info">
                  <span className="financial-card-label" style={{ color: '#581c87' }}>Toplam Alım Tutarı</span>
                  <span className="financial-card-value" style={{ color: '#6b21a8' }}>{fmtCurrency(group.total, cur)}</span>
                </div>
              </div>
              <div className="financial-card paid-card" style={{ background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)', borderColor: '#bbf7d0' }}>
                <div className="financial-card-icon" style={{ background: '#dcfce7', color: '#166534' }}><i className="fas fa-hand-holding-usd"></i></div>
                <div className="financial-card-info">
                  <span className="financial-card-label" style={{ color: '#14532d' }}>Ödenen (Bizim Ödediğimiz)</span>
                  <span className="financial-card-value" style={{ color: '#16a34a' }}>{fmtCurrency(group.paid, cur)}</span>
                  {group.directPaid > 0 && (
                    <span className="financial-card-subhint" style={{ fontSize: '0.75rem', color: '#059669', opacity: 0.9 }}>
                      (Alım: {fmtCurrency(group.sessionPaid, cur)} + Harici: {fmtCurrency(group.directPaid, cur)})
                    </span>
                  )}
                </div>
              </div>
              <div className={`financial-card ${isCreditor ? 'credit-card' : 'remaining-card'}`} style={{
                background: isCreditor ? 'linear-gradient(135deg, #fff7ed, #ffedd5)' : 'linear-gradient(135deg, #fef2f2, #fee2e2)',
                borderColor: isCreditor ? '#fed7aa' : '#fca5a5'
              }}>
                <div className="financial-card-icon" style={{
                  background: isCreditor ? '#ffedd5' : '#fee2e2',
                  color: isCreditor ? '#c2410c' : '#991b1b'
                }}>
                  <i className={isCreditor ? 'fas fa-hand-holding-usd' : 'fas fa-exclamation-circle'}></i>
                </div>
                <div className="financial-card-info">
                  <span className="financial-card-label" style={{ color: isCreditor ? '#9a3412' : '#7f1d1d' }}>
                    {isCreditor ? 'Tedarikçi Bize Borçlu' : 'Kalan Borcumuz'}
                  </span>
                  <span className="financial-card-value" style={{ color: isCreditor ? '#ea580c' : '#dc2626' }}>
                    {fmtCurrency(isCreditor ? creditBalance : openBalance, cur)}
                  </span>
                  {isBalanced && (
                    <span className="financial-card-hint">Bakiye kapandı</span>
                  )}
                </div>
              </div>
            </div>
            );
          })}
        </>
      )}

      {/* Harici / Manuel Yapılan Ödemeler Accordion */}
      <div className="dealer-direct-payment-accordion" style={{ borderColor: '#ddd6fe' }}>
        <div 
          className={`direct-payment-accordion-header ${isDirectPaymentAccordionOpen ? 'open' : ''}`}
          onClick={() => setIsDirectPaymentAccordionOpen(prev => !prev)}
          style={{ background: isDirectPaymentAccordionOpen ? 'linear-gradient(135deg, #f5f3ff, #ede9fe)' : 'linear-gradient(135deg, #faf5ff, #f3e8ff)' }}
        >
          <div className="accordion-title-group">
            <i className="fas fa-hand-holding-usd accordion-icon" style={{ color: '#8b5cf6' }}></i>
            <span className="accordion-title" style={{ color: '#581c87' }}>Harici / Manuel Yapılan Ödemeler</span>
            <span className="accordion-badge" style={{ background: '#ede9fe', color: '#6d28d9' }}>
              {supplierDirectPayments.length} Kayıt | Toplam: {totalDirectPaidByCurrencies}
            </span>
          </div>
          <div className="accordion-toggle-icon">
            <i className={`fas ${isDirectPaymentAccordionOpen ? 'fa-chevron-up' : 'fa-chevron-down'}`}></i>
          </div>
        </div>

        {isDirectPaymentAccordionOpen && (
          <div className="direct-payment-accordion-content">
            {/* Ödeme Ekleme Formu */}
            <form onSubmit={handleAddDirectPayment} className="direct-payment-form">
              <div className="form-group">
                <label>Ödeme Tutarı</label>
                <input
                  type="number"
                  step="any"
                  placeholder="0.00"
                  value={newDirectPaymentAmount}
                  onChange={(e) => setNewDirectPaymentAmount(e.target.value)}
                  disabled={isAddingDirectPayment}
                  required
                />
              </div>
              <div className="form-group">
                <label>Para Birimi</label>
                <select
                  value={newDirectPaymentCurrency}
                  onChange={(e) => setNewDirectPaymentCurrency(e.target.value)}
                  disabled={isAddingDirectPayment}
                >
                  <option value="TRY">₺ TL (TRY)</option>
                  <option value="USD">$ USD</option>
                  <option value="EUR">€ EUR</option>
                </select>
              </div>
              <div className="form-group">
                <label>Ödeme Tipi</label>
                <select
                  value={newDirectPaymentType}
                  onChange={(e) => setNewDirectPaymentType(e.target.value)}
                  disabled={isAddingDirectPayment}
                >
                  <option value="Nakit">Nakit</option>
                  <option value="Kredi Kartı">Kredi Kartı</option>
                  <option value="Havale / EFT">Havale / EFT</option>
                  <option value="Çek">Çek</option>
                  <option value="Senet">Senet</option>
                </select>
              </div>
              <div className="form-group">
                <label>Ödeme Tarihi</label>
                <input
                  type="date"
                  value={newDirectPaymentDate}
                  onChange={(e) => setNewDirectPaymentDate(e.target.value)}
                  disabled={isAddingDirectPayment}
                  required
                />
              </div>
              <div className="form-group wide">
                <label>Açıklama / Not (Opsiyonel)</label>
                <input
                  type="text"
                  placeholder="Örn: Tedarikçi cari bakiye ödemesi, Banka havalesi..."
                  value={newDirectPaymentNote}
                  onChange={(e) => setNewDirectPaymentNote(e.target.value)}
                  disabled={isAddingDirectPayment}
                />
              </div>
              <div className="form-group button-group direct-payment-actions-row">
                <button
                  type="submit"
                  className="btn-add-direct-payment"
                  style={{ background: '#8b5cf6' }}
                  disabled={isAddingDirectPayment}
                >
                  {isAddingDirectPayment ? (
                    <i className="fas fa-spinner fa-spin"></i>
                  ) : (
                    <>
                      <i className="fas fa-plus-circle"></i> Ödeme Ekle
                    </>
                  )}
                </button>

                <div className="direct-pdf-buttons-group">
                  <div className="direct-pdf-date-inputs">
                    <div className="pdf-date-input-item">
                      <label>Başlangıç</label>
                      <input
                        type="date"
                        value={directPdfDateFrom}
                        onChange={(e) => setDirectPdfDateFrom(e.target.value)}
                        disabled={isGeneratingDirectPdf}
                      />
                    </div>
                    <div className="pdf-date-input-item">
                      <label>Bitiş</label>
                      <input
                        type="date"
                        value={directPdfDateTo}
                        onChange={(e) => setDirectPdfDateTo(e.target.value)}
                        disabled={isGeneratingDirectPdf}
                      />
                    </div>
                  </div>

                  <button
                    type="button"
                    className="btn-direct-pdf btn-direct-pdf-range"
                    onClick={() => handleGenerateDirectPaymentPDF('range')}
                    disabled={isGeneratingDirectPdf}
                    title="Seçili tarih aralığındaki harici ödemeleri PDF olarak çıkar"
                  >
                    {isGeneratingDirectPdf ? (
                      <><i className="fas fa-spinner fa-spin"></i> Hazırlanıyor...</>
                    ) : (
                      <><i className="fas fa-calendar-alt"></i> Tarih Aralığı PDF</>
                    )}
                  </button>

                  <button
                    type="button"
                    className="btn-direct-pdf btn-direct-pdf-all"
                    onClick={() => handleGenerateDirectPaymentPDF('all')}
                    disabled={isGeneratingDirectPdf}
                    title="Tüm harici ödemeleri kapsayan PDF raporu çıkar"
                  >
                    {isGeneratingDirectPdf ? (
                      <><i className="fas fa-spinner fa-spin"></i> Hazırlanıyor...</>
                    ) : (
                      <><i className="fas fa-file-pdf"></i> Tüm Ödemeler PDF</>
                    )}
                  </button>
                </div>
              </div>
            </form>

            {/* Ödeme Listesi & Tablo */}
            {supplierDirectPayments.length === 0 ? (
              <div className="direct-payments-empty">
                <i className="fas fa-receipt" style={{ color: '#c4b5fd' }}></i>
                <p>Henüz harici/manuel ödeme eklenmemiş.</p>
              </div>
            ) : (
              <>
                <div className="direct-payments-table-wrapper">
                  <table className="direct-payments-table">
                    <thead>
                      <tr>
                        <th>Ödeme Tarihi</th>
                        <th>Kur</th>
                        <th>Ödeme Tipi</th>
                        <th>Açıklama</th>
                        <th>Tutar</th>
                        <th>İşlemler</th>
                      </tr>
                    </thead>
                    <tbody>
                      {supplierDirectPayments
                        .slice(
                          (directPaymentPage - 1) * DIRECT_PAYMENT_PAGE_SIZE,
                          directPaymentPage * DIRECT_PAYMENT_PAGE_SIZE
                        )
                        .map((payment) => {
                          const isEditing = editingDirectPayment?.id === payment.id;
                          const dateFormatted = payment.date
                            ? new Date(`${payment.date}T00:00:00`).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })
                            : (payment.createdAt ? new Date(payment.createdAt).toLocaleDateString('tr-TR') : '—');
                          const cur = payment.currency || 'TRY';

                          if (isEditing) {
                            return (
                              <tr key={payment.id} className="editing-row">
                                <td>
                                  <input
                                    type="date"
                                    className="edit-date-input"
                                    value={editDirectPaymentDate}
                                    onChange={(e) => setEditDirectPaymentDate(e.target.value)}
                                  />
                                </td>
                                <td>
                                  <select
                                    className="edit-type-select"
                                    value={editDirectPaymentCurrency}
                                    onChange={(e) => setEditDirectPaymentCurrency(e.target.value)}
                                  >
                                    <option value="TRY">₺ TL</option>
                                    <option value="USD">$ USD</option>
                                    <option value="EUR">€ EUR</option>
                                  </select>
                                </td>
                                <td>
                                  <select
                                    className="edit-type-select"
                                    value={editDirectPaymentType}
                                    onChange={(e) => setEditDirectPaymentType(e.target.value)}
                                  >
                                    <option value="Nakit">Nakit</option>
                                    <option value="Kredi Kartı">Kredi Kartı</option>
                                    <option value="Havale / EFT">Havale / EFT</option>
                                    <option value="Çek">Çek</option>
                                    <option value="Senet">Senet</option>
                                  </select>
                                </td>
                                <td>
                                  <input
                                    type="text"
                                    className="edit-note-input"
                                    value={editDirectPaymentNote}
                                    onChange={(e) => setEditDirectPaymentNote(e.target.value)}
                                    placeholder="Açıklama"
                                  />
                                </td>
                                <td>
                                  <input
                                    type="number"
                                    step="any"
                                    className="edit-amount-input"
                                    value={editDirectPaymentAmount}
                                    onChange={(e) => setEditDirectPaymentAmount(e.target.value)}
                                  />
                                </td>
                                <td className="actions-cell">
                                  <button
                                    type="button"
                                    className="btn-save-inline"
                                    title="Kaydet"
                                    onClick={handleSaveEditedDirectPayment}
                                  >
                                    <i className="fas fa-check"></i>
                                  </button>
                                  <button
                                    type="button"
                                    className="btn-cancel-inline"
                                    title="İptal"
                                    onClick={() => setEditingDirectPayment(null)}
                                  >
                                    <i className="fas fa-times"></i>
                                  </button>
                                </td>
                              </tr>
                            );
                          }

                          return (
                            <tr key={payment.id}>
                              <td data-label="Ödeme Tarihi" className="date-cell">
                                <i className="far fa-calendar-alt"></i> {dateFormatted}
                              </td>
                              <td data-label="Kur">
                                <span style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  background: cur === 'TRY' ? '#f0fdf4' : cur === 'USD' ? '#eff6ff' : '#fdf4ff',
                                  color: cur === 'TRY' ? '#166534' : cur === 'USD' ? '#1d4ed8' : '#7e22ce',
                                  border: `1px solid ${cur === 'TRY' ? '#bbf7d0' : cur === 'USD' ? '#bfdbfe' : '#e9d5ff'}`,
                                  borderRadius: '6px',
                                  padding: '2px 8px',
                                  fontSize: '0.85rem',
                                  fontWeight: 700
                                }}>
                                  {CURRENCY_SYMBOLS[cur] || cur}
                                </span>
                              </td>
                              <td data-label="Ödeme Tipi">
                                <span className="payment-type-pill">{payment.type || 'Nakit'}</span>
                              </td>
                              <td data-label="Açıklama" className="note-cell">
                                {payment.notes || '—'}
                              </td>
                              <td data-label="Tutar" className="amount-cell">
                                {fmtCurrency(payment.amount, cur)}
                              </td>
                              <td data-label="İşlemler" className="actions-cell">
                                <button
                                  type="button"
                                  className="btn-edit-payment"
                                  title="Ödeme Tarihini / Bilgilerini Düzenle"
                                  onClick={() => {
                                    setEditingDirectPayment(payment);
                                    setEditDirectPaymentAmount(payment.amount?.toString() || '');
                                    setEditDirectPaymentCurrency(payment.currency || 'TRY');
                                    setEditDirectPaymentType(payment.type || 'Nakit');
                                    setEditDirectPaymentDate(payment.date || '');
                                    setEditDirectPaymentNote(payment.notes || '');
                                  }}
                                >
                                  <i className="fas fa-edit"></i>
                                </button>
                                <button
                                  type="button"
                                  className="btn-delete-payment"
                                  title="Ödemeyi Sil"
                                  onClick={() => setDeleteDirectPaymentConfirm(payment)}
                                >
                                  <i className="fas fa-trash-alt"></i>
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>

                {/* Pagination (5 satır/sayfa) */}
                {supplierDirectPayments.length > DIRECT_PAYMENT_PAGE_SIZE && (
                  <div className="direct-payments-pagination">
                    <button
                      type="button"
                      className="page-btn"
                      onClick={() => setDirectPaymentPage(p => Math.max(1, p - 1))}
                      disabled={directPaymentPage === 1}
                    >
                      <i className="fas fa-chevron-left"></i>
                    </button>

                    {Array.from({ length: Math.ceil(supplierDirectPayments.length / DIRECT_PAYMENT_PAGE_SIZE) }, (_, i) => i + 1).map(page => (
                      <button
                        type="button"
                        key={page}
                        className={`page-btn ${directPaymentPage === page ? 'active' : ''}`}
                        onClick={() => setDirectPaymentPage(page)}
                        style={{
                          background: directPaymentPage === page ? '#8b5cf6' : '#f8fafc',
                          borderColor: directPaymentPage === page ? '#8b5cf6' : '#e2e8f0'
                        }}
                      >
                        {page}
                      </button>
                    ))}

                    <button
                      type="button"
                      className="page-btn"
                      onClick={() => setDirectPaymentPage(p => Math.min(Math.ceil(supplierDirectPayments.length / DIRECT_PAYMENT_PAGE_SIZE), p + 1))}
                      disabled={directPaymentPage === Math.ceil(supplierDirectPayments.length / DIRECT_PAYMENT_PAGE_SIZE)}
                    >
                      <i className="fas fa-chevron-right"></i>
                    </button>

                    <span className="page-info">
                      {(directPaymentPage - 1) * DIRECT_PAYMENT_PAGE_SIZE + 1}–{Math.min(directPaymentPage * DIRECT_PAYMENT_PAGE_SIZE, supplierDirectPayments.length)} / {supplierDirectPayments.length} kayıt
                    </span>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="dealer-loading-container">
          <i className="fas fa-spinner fa-spin fa-2x" style={{ color: '#94a3b8' }}></i>
          <p style={{ color: '#64748b', marginTop: '10px' }}>Alım listeleri yükleniyor...</p>
        </div>
      ) : sessions.length === 0 ? (
        <div className="empty-state">
          <i className="fas fa-box-open" style={{ color: '#ddd6fe' }}></i>
          <p>Henüz alım listesi bulunmuyor. Yeni bir alım listesi oluşturarak tedarikçi alımlarınızı kaydedin.</p>
        </div>
      ) : (
        <table className="sessions-table">
          <thead>
            <tr>
              <th>Tarih</th>
              <th>Ürün Adı</th>
              <th>Kur</th>
              <th>Toplam Alım</th>
              <th>Ödenen / Borcumuz</th>
              <th>İşlemler</th>
            </tr>
          </thead>
          <tbody>
            {sortedSessions
              .slice((sessionPage - 1) * SESSION_PAGE_SIZE, sessionPage * SESSION_PAGE_SIZE)
              .map(session => {
                const cur = session.currency || 'TRY';
                const totalAmt = parseFloat(session.totalOrderAmount || 0);
                const paid = parseFloat(session.paidAmount || 0);
                const remaining = getSupplierSessionRemaining(session);
                const balanceInfo = formatSupplierBalanceLabel(remaining);
                const sessionDateStr = getSessionDateStr(session);
                const productInfo = formatSessionProductNames(session.productNames);

                return (
                  <tr key={session.id}>
                    <td className="session-date" data-label="Tarih">
                      <input
                        type="date"
                        className="session-date-input"
                        value={sessionDateStr}
                        onChange={(e) => handleSessionDateChange(session.id, e.target.value)}
                        disabled={savingSessionDateId === session.id}
                        title="Alım tarihini düzenle"
                      />
                    </td>
                    <td className="session-product-names" data-label="Ürün Adı" title={productInfo.title}>
                      {productInfo.text}
                    </td>
                    <td data-label="Kur">
                      <span
                        title={cur}
                        style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: cur === 'TRY' ? '#f0fdf4' : cur === 'USD' ? '#eff6ff' : '#fdf4ff',
                        color: cur === 'TRY' ? '#166534' : cur === 'USD' ? '#1d4ed8' : '#7e22ce',
                        border: `1px solid ${cur === 'TRY' ? '#bbf7d0' : cur === 'USD' ? '#bfdbfe' : '#e9d5ff'}`,
                        borderRadius: '6px',
                        padding: '3px 10px',
                        fontSize: '0.9rem',
                        fontWeight: 700,
                        whiteSpace: 'nowrap',
                        minWidth: '32px'
                      }}>
                        {CURRENCY_SYMBOLS[cur] || cur}
                      </span>
                    </td>
                    <td className="finance-cell text-gray" data-label="Toplam Alım">
                      {fmtCurrency(totalAmt, cur)}
                    </td>
                    <td className="finance-cell" data-label="Ödenen / Borcumuz">
                      <span className="text-green">{fmtCurrency(paid, cur)}</span>
                      <span style={{ color: '#cbd5e1', margin: '0 8px' }}>|</span>
                      {balanceInfo.type === 'credit' ? (
                        <span className="text-credit" title="Fazla ödeme — tedarikçi bize borçlu">
                          <i className="fas fa-hand-holding-usd" style={{ marginRight: '4px', fontSize: '0.75rem' }}></i>
                          {fmtCurrency(balanceInfo.amount, cur)} alacak
                        </span>
                      ) : balanceInfo.type === 'balanced' ? (
                        <span style={{ color: '#64748b', fontWeight: 600 }}>Dengede</span>
                      ) : (
                        <span className="text-red">{fmtCurrency(balanceInfo.amount, cur)}</span>
                      )}
                    </td>
                    <td className="session-actions" data-label="İşlemler">
                      <button
                        type="button"
                        className="btn-detail"
                        onClick={() => setSelectedSession(session)}
                        style={{ border: '1px solid #ddd6fe', background: '#f5f3ff' }}
                      >
                        <i className="fas fa-external-link-alt" style={{ color: '#8b5cf6' }}></i> Alımları Gör
                      </button>
                      <button
                        type="button"
                        className="btn-delete-session"
                        onClick={() => setDeleteSessionConfirm(session)}
                      >
                        <i className="fas fa-trash-alt"></i>
                      </button>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      )}

      {/* Sayfalama */}
      {sortedSessions.length > SESSION_PAGE_SIZE && (
        <div className="sessions-pagination">
          <button
            type="button"
            className="page-btn"
            onClick={() => setSessionPage(p => Math.max(1, p - 1))}
            disabled={sessionPage === 1}
          >
            <i className="fas fa-chevron-left"></i>
          </button>

          {Array.from({ length: Math.ceil(sortedSessions.length / SESSION_PAGE_SIZE) }, (_, i) => i + 1).map(page => (
            <button
              type="button"
              key={page}
              className={`page-btn ${sessionPage === page ? 'active' : ''}`}
              onClick={() => setSessionPage(page)}
              style={{
                background: sessionPage === page ? '#8b5cf6' : '#f8fafc',
                borderColor: sessionPage === page ? '#8b5cf6' : '#e2e8f0'
              }}
            >
              {page}
            </button>
          ))}

          <button
            type="button"
            className="page-btn"
            onClick={() => setSessionPage(p => Math.min(Math.ceil(sortedSessions.length / SESSION_PAGE_SIZE), p + 1))}
            disabled={sessionPage === Math.ceil(sortedSessions.length / SESSION_PAGE_SIZE)}
          >
            <i className="fas fa-chevron-right"></i>
          </button>

          <span className="page-info">
            {(sessionPage - 1) * SESSION_PAGE_SIZE + 1}–{Math.min(sessionPage * SESSION_PAGE_SIZE, sortedSessions.length)} / {sortedSessions.length} kayıt
          </span>
        </div>
      )}

      {/* Harici Ödeme Silme Onayı */}
      {deleteDirectPaymentConfirm && (
        <div className="order-details-modal-overlay" style={{ zIndex: 1100 }}>
          <div className="order-details-modal-content" style={{ maxWidth: '500px', height: 'auto', padding: '30px' }}>
            <h3 style={{ color: '#ef4444', marginBottom: '15px' }}><i className="fas fa-exclamation-triangle"></i> Harici Ödemeyi Sil</h3>
            <p style={{ color: '#475569', lineHeight: '1.6' }}>
              Bu tedarikçi harici ödeme kaydını ({fmtCurrency(deleteDirectPaymentConfirm.amount, deleteDirectPaymentConfirm.currency)}) silmek istediğinize emin misiniz? Bu işlem geri alınamaz.
            </p>
            <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end', marginTop: '25px' }}>
              <button
                type="button"
                className="cancel-btn"
                onClick={() => setDeleteDirectPaymentConfirm(null)}
              >
                İptal
              </button>
              <button
                type="button"
                className="save-btn"
                style={{ background: '#dc2626' }}
                onClick={() => handleDeleteDirectPayment(deleteDirectPaymentConfirm.id)}
              >
                Evet, Sil
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Silme Onayı */}
      {deleteSessionConfirm && (
        <div className="order-details-modal-overlay" style={{ zIndex: 1100 }}>
          <div className="order-details-modal-content" style={{ maxWidth: '500px', height: 'auto', padding: '30px' }}>
            <h3 style={{ color: '#ef4444', marginBottom: '15px' }}><i className="fas fa-exclamation-triangle"></i> Alım Listesini Sil</h3>
            <p style={{ color: '#475569', lineHeight: '1.6' }}>
              Bu alım listesini ve içindeki tüm ürün alımlarını silmek istediğinize emin misiniz? Bu işlem geri alınamaz.
            </p>
            <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end', marginTop: '25px' }}>
              <button
                type="button"
                className="cancel-btn"
                onClick={() => setDeleteSessionConfirm(null)}
              >
                İptal
              </button>
              <button
                type="button"
                className="save-btn"
                style={{ background: '#dc2626' }}
                onClick={handleDeleteSession}
              >
                Evet, Sil
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tedarikçi Alım Detayları Modal */}
      {selectedSession && (
        <SupplierOrderModal
          session={selectedSession}
          onClose={() => {
            setSelectedSession(null);
            fetchSessions(dealer.id);
            if (onCustomerChange) {
              onCustomerChange();
            }
          }}
          onSessionDateChange={handleSessionDateChange}
          db={db}
          user={user}
        />
      )}
    </div>
  );
};

export default SupplierSessionPanel;
